import type { CartOrderType } from '@order-up/models';
import { Cart } from '@order-up/models';
import { createLogger } from '../utils/logger';
import { counterService } from './counters';

const logger = createLogger('CartStorage');

// Browser environment check.
// IMPORTANT: accessing `window.localStorage` (even via `typeof`) invokes the
// getter, which THROWS `SecurityError` in storage-blocked contexts — sandboxed
// iframes without allow-same-origin, and several mobile in-app webviews
// (Instagram/Facebook/TikTok) with cookies/storage disabled. Because this runs
// at module load, an unguarded throw would break the import of this module (and
// everything that depends on it: cart.ts → useCart), taking down the whole cart
// subsystem on exactly those mobile browsers. Probe defensively with a real
// write/remove so a store that throws on write is also treated as unavailable.
const isBrowser: boolean = (() => {
    try {
        if (typeof window === 'undefined' || !window.localStorage) return false;
        const probeKey = '__order-up-ls-probe__';
        window.localStorage.setItem(probeKey, '1');
        window.localStorage.removeItem(probeKey);
        return true;
    } catch {
        return false;
    }
})();

/**
 * Service for managing cart persistence in local storage
 * Cart expiration is handled server-side via Firestore TTL based on expiresAt field
 */
export class CartStorageService {
    private static readonly CART_KEY = 'order-up-cart';
    private static readonly CART_EXPIRY_DAYS = 30; // Server TTL window

    /**
     * Save cart to local storage
     */
    static saveCart(cart: Cart): void {
        if (!isBrowser) {
            logger.warn('localStorage not available in this environment');
            return;
        }

        try {
            const cartData = {
                cart: cart.toJSON(),
                savedAt: new Date().toISOString()
            };
            const dataToSave = JSON.stringify(cartData);
            logger.debug('Saving cart to localStorage', {
                key: this.CART_KEY,
                itemCount: cart.items.length,
                totalItems: cart.itemCount,
                dataSize: dataToSave.length,
                cartId: cart.cartId
            });
            window.localStorage.setItem(this.CART_KEY, dataToSave);
            logger.info('Successfully saved cart to localStorage');
        } catch (error) {
            logger.warn('Failed to save cart to localStorage', { error: error instanceof Error ? error.message : String(error) });
        }
    }

    /**
     * Load cart from local storage
     */
    static loadCart(): Cart | null {
        if (!isBrowser) {
            logger.debug('Not in browser environment, returning null');
            return null;
        }

        try {
            logger.debug('Loading cart from localStorage');
            const stored = window.localStorage.getItem(this.CART_KEY);
            logger.debug('Raw localStorage data', { hasData: !!stored, dataLength: stored?.length || 0 });

            if (!stored) {
                logger.debug('No cart found in localStorage');
                return null;
            }

            const cartData = JSON.parse(stored);
            logger.debug('Parsed cart data', {
                hasCart: !!cartData.cart,
                savedAt: cartData.savedAt,
                itemCount: cartData.cart?.items?.length || 0
            });

            const cart = Cart.fromJSON(cartData.cart as Record<string, unknown>);
            logger.debug('Created cart object', {
                cartId: cart.cartId,
                itemCount: cart.items.length,
                totalItems: cart.itemCount,
                isGuest: cart.isGuest
            });

            logger.info('Successfully loaded cart from localStorage');
            return cart;
        } catch (error) {
            logger.warn('Failed to load cart from localStorage', { error: error instanceof Error ? error.message : String(error) });
            this.clearCart(); // Clear corrupted data
            return null;
        }
    }

    /**
     * Clear cart from local storage
     */
    static clearCart(): void {
        if (!isBrowser) {
            return;
        }

        try {
            window.localStorage.removeItem(this.CART_KEY);
        } catch (error) {
            logger.warn('Failed to clear cart from localStorage', { error: error instanceof Error ? error.message : String(error) });
        }
    }

    /**
     * Check if there's a saved cart
     */
    static hasCart(): boolean {
        if (!isBrowser) {
            return false;
        }

        try {
            return window.localStorage.getItem(this.CART_KEY) !== null;
        } catch {
            return false;
        }
    }

    /**
     * Get cart expiry time for server TTL
     */
    static getCartExpiry(): Date {
        const expiry = new Date();
        expiry.setDate(expiry.getDate() + this.CART_EXPIRY_DAYS);
        return expiry;
    }

    /**
     * Extend cart expiration (updates expiresAt for server TTL)
     * Only applies to guest carts - authenticated user carts don't expire
     */
    static extendCartExpiry(cart: Cart): Cart {
        // Only set expiration for guest users
        // Authenticated users' carts never expire on server
        if (cart.isGuest) {
            const extendedCart = cart.withExpiration(this.getCartExpiry());
            this.saveCart(extendedCart);
            return extendedCart;
        }

        // For authenticated users, just save without extending
        this.saveCart(cart);
        return cart;
    }

    /**
     * Create a new cart with expiration set for server TTL (guest users only)
     * Uses counterService to generate cartId and guid
     * Guest carts: expiresAt set → server TTL deletes after 30 days
     * Authenticated carts: no expiresAt → persist indefinitely
     * @param isAnonymous - Whether the user is anonymous (guest) or authenticated (logged in)
     */
    static async createCartWithExpiry(
        locationNumericId: number,
        tenantId: number,
        orderType: CartOrderType = 'pickup',
        customerId?: string,
        isAnonymous?: boolean,
        /**
         * `dineIn` only — the table the guest scanned. Stamped at CREATION
         * because a table cart that forgets its table is a guest who scanned a
         * code and still cannot order; the checkout route refuses it.
         */
        tableGuid?: string,
    ): Promise<Cart> {
        // Generate cart ID using counter service
        const { cartId, guid } = await counterService.generateCartId(tenantId, locationNumericId);

        const isGuest = isAnonymous ?? !customerId;

        const cart = new Cart({
            cartId,
            guid,
            customerId,
            restaurantId: locationNumericId,
            tenantId,
            orderType,
            ...(tableGuid !== undefined ? { tableGuid } : {}),
            // isGuest is true if explicitly passed OR if no customerId exists (fallback)
            // Anonymous auth users have customerId but are still guests
            isGuest
        });

        // Only set expiration for guest users
        // Authenticated users' carts don't have expiresAt → never deleted by server TTL
        if (isGuest) {
            return cart.withExpiration(this.getCartExpiry());
        }

        return cart;
    }

    /**
     * Create a guest cart SYNCHRONOUSLY, with NO Firestore round-trip.
     *
     * Unlike createCartWithExpiry (which calls counterService.generateCartId →
     * several Firestore reads), this needs no network and no auth, so a guest can
     * start adding items immediately — even before anonymous auth resolves, or in
     * a browser where Firebase/Firestore is slow or blocked. The numeric cartId
     * is a client-side temporary; the real numeric id is allocated lazily only if
     * the cart is ever synced to Firestore (which only happens once authenticated,
     * via upsertCartInFirestore). The unguessable `guid` is the cart's durable key
     * for checkout handoff.
     */
    static createLocalGuestCart(
        locationNumericId: number,
        tenantId: number,
        orderType: CartOrderType = 'pickup',
        customerId?: string,
        isAnonymous?: boolean,
        /** `dineIn` only — see {@link createCartWithExpiry}. Last, so existing
         *  positional callers are untouched. */
        tableGuid?: string,
    ): Cart {
        const isGuest = isAnonymous ?? !customerId;
        const cart = new Cart({
            // Client-side temporary id (uniqueness is per-browser-session; the
            // durable cross-app key is `guid`). Negative-safe: Date.now() is a
            // large positive integer, satisfying Cart's `cartId: number` contract.
            cartId: Date.now(),
            guid: this.generateLocalGuid(),
            customerId,
            restaurantId: locationNumericId,
            tenantId,
            orderType,
            ...(tableGuid !== undefined ? { tableGuid } : {}),
            isGuest,
        });
        return isGuest ? cart.withExpiration(this.getCartExpiry()) : cart;
    }

    /** RFC4122 v4 GUID, with a fallback for non-secure contexts lacking crypto.randomUUID. */
    private static generateLocalGuid(): string {
        try {
            if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
                return crypto.randomUUID();
            }
        } catch {
            /* fall through to manual generation */
        }
        return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
            const r = (Math.random() * 16) | 0;
            const v = c === 'x' ? r : (r & 0x3) | 0x8;
            return v.toString(16);
        });
    }
}
